import React, { useEffect, useMemo, useState } from "react";
import { GiftIcon, TrophyIcon, SparkleIcon } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";

/**
 * Floating live-show giveaway banner.
 *
 * Anchors to the top of the video stage. Renders three states:
 *  • ACTIVE  — big GIFT pill, countdown timer, entries count, TAP TO ENTER button.
 *              Buyer can tap to enter (idempotent — already-entered turns to YOU'RE IN).
 *  • PICKING — momentary "Picking a winner…" sparkle state.
 *  • ENDED   — winner reveal card with confetti SFX. Auto-dismisses after 10s.
 *
 * Driven by the stream's WS feed:
 *   giveaway.started → load(this prop)
 *   giveaway.entry   → bump entries_count
 *   giveaway.ended   → flip to ended view + show winner
 */
export default function GiveawayBanner({ giveaway, currentUser, isHost, onUpdate }) {
  const [now, setNow] = useState(() => Date.now());
  const [entering, setEntering] = useState(false);
  const [picking, setPicking] = useState(false);

  useEffect(() => {
    if (!giveaway || giveaway.status !== "active") return;
    const t = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(t);
  }, [giveaway]);

  const endsAtMs = useMemo(
    () => (giveaway?.ends_at ? new Date(giveaway.ends_at).getTime() : 0),
    [giveaway?.ends_at],
  );
  const remainingMs = Math.max(0, endsAtMs - now);
  const remainingLabel = useMemo(() => {
    const total = Math.ceil(remainingMs / 1000);
    if (total >= 60) {
      const m = Math.floor(total / 60);
      const s = String(total % 60).padStart(2, "0");
      return `${m}:${s}`;
    }
    return `${total}s`;
  }, [remainingMs]);

  if (!giveaway) return null;

  const onEnter = async () => {
    if (entering || giveaway.you_are_in) return;
    setEntering(true);
    try {
      const { data } = await api.post(`/giveaways/${giveaway.id}/enter`);
      onUpdate?.(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setEntering(false);
    }
  };

  const onPick = async () => {
    if (picking) return;
    setPicking(true);
    try {
      const { data } = await api.post(`/giveaways/${giveaway.id}/pick`);
      onUpdate?.(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setPicking(false);
    }
  };

  // ── ENDED state — winner reveal ────────────────────────────────────
  if (giveaway.status === "ended") {
    const winnerName = giveaway.winner_username || giveaway.winner_name;
    return (
      <motion.div
        // NOTE: `x: "-50%"` is required because Framer Motion's transform
        // overrides our CSS `translateX(-50%)`, otherwise the pill drifts
        // off the right edge of the viewport on mobile.
        initial={{ opacity: 0, y: -10, scale: 0.9, x: "-50%" }}
        animate={{ opacity: 1, y: 0, scale: 1, x: "-50%" }}
        exit={{ opacity: 0, y: -10, x: "-50%" }}
        className="bidora-giveaway-banner bg-amber-400 text-black"
        data-testid="giveaway-ended-banner"
      >
        <TrophyIcon size={16} weight="fill" />
        <div className="flex-1 min-w-0">
          <div className="text-[9px] font-bold uppercase tracking-wider opacity-70 leading-none">Giveaway winner</div>
          <div className="text-[12px] font-black truncate leading-tight">
            {winnerName ? <>@{winnerName}</> : <>No entries — no winner</>}
          </div>
          {winnerName && (
            <div className="text-[10px] font-semibold opacity-80 truncate leading-tight">
              won {giveaway.prize}
            </div>
          )}
        </div>
        <SparkleIcon size={14} weight="fill" className="animate-pulse" />
      </motion.div>
    );
  }

  // ── ACTIVE state ───────────────────────────────────────────────────
  return (
    <motion.div
      // `x: "-50%"` keeps the pill horizontally centered — Framer's transform
      // would otherwise clobber the CSS translateX(-50%).
      initial={{ opacity: 0, y: -8, scale: 0.95, x: "-50%" }}
      animate={{ opacity: 1, y: 0, scale: 1, x: "-50%" }}
      exit={{ opacity: 0, y: -8, x: "-50%" }}
      className="bidora-giveaway-banner"
      data-testid="giveaway-active-banner"
    >
      <span className="bidora-giveaway-icon">
        <GiftIcon size={14} weight="fill" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-[9px] font-bold uppercase tracking-wider opacity-70 leading-none">
          Giveaway · {giveaway.entries_count} {giveaway.entries_count === 1 ? "entry" : "entries"}
        </div>
        <div className="text-[12px] font-black truncate leading-tight">{giveaway.prize}</div>
      </div>
      <div className="bidora-giveaway-timer" data-testid="giveaway-timer">
        {remainingLabel}
      </div>
      {isHost ? (
        <button
          type="button"
          onClick={onPick}
          disabled={picking}
          className="bidora-giveaway-cta bg-zinc-900 text-white hover:bg-zinc-800 disabled:opacity-60"
          data-testid="giveaway-pick-btn"
        >
          {picking ? "Picking…" : "Pick now"}
        </button>
      ) : !currentUser ? (
        <a
          href="/login"
          className="bidora-giveaway-cta bg-white text-black hover:bg-zinc-100"
          data-testid="giveaway-signin-btn"
        >
          Sign in
        </a>
      ) : giveaway.you_are_in ? (
        <span
          className="bidora-giveaway-cta bg-emerald-500 text-white pointer-events-none"
          data-testid="giveaway-entered-state"
        >
          ✓ You're in
        </span>
      ) : (
        <button
          type="button"
          onClick={onEnter}
          disabled={entering}
          className="bidora-giveaway-cta bg-amber-400 text-black hover:bg-amber-300 disabled:opacity-60"
          data-testid="giveaway-enter-btn"
        >
          {entering ? "Joining…" : "Join"}
        </button>
      )}
    </motion.div>
  );
}

/**
 * Modal for a seller to start a new giveaway.  Prize text + duration
 * preset buttons.  Submits then the banner takes over.
 */
export function StartGiveawayDialog({ open, onOpenChange, streamId, onStarted }) {
  const [prize, setPrize] = useState("");
  const [duration, setDuration] = useState(60);
  const [busy, setBusy] = useState(false);
  if (!open) return null;

  const submit = async () => {
    if (busy) return;
    const trimmed = prize.trim();
    if (trimmed.length < 2) {
      toast.error("Add a prize description (2+ characters).");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/streams/${streamId}/giveaways`, {
        prize: trimmed,
        duration_seconds: duration,
      });
      toast.success("Giveaway started 🎁");
      onStarted?.(data);
      setPrize("");
      onOpenChange(false);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[2000] grid place-items-end md:place-items-center bg-black/60"
      onClick={() => onOpenChange(false)}
      data-testid="start-giveaway-dialog"
    >
      <div
        className="w-full md:max-w-md bg-white rounded-t-2xl md:rounded-2xl p-5 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2">
          <span className="h-9 w-9 grid place-items-center rounded-full bg-amber-400 text-black">
            <GiftIcon size={20} weight="fill" />
          </span>
          <div>
            <div className="text-lg font-black leading-tight">Start a giveaway</div>
            <div className="text-xs text-zinc-500 leading-tight">Viewers tap to enter. One random winner.</div>
          </div>
        </div>

        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Prize</label>
          <input
            type="text"
            value={prize}
            onChange={(e) => setPrize(e.target.value)}
            placeholder="e.g. $25 Bidorah credit · Mystery card · Free shipping"
            maxLength={140}
            className="w-full h-11 px-3 mt-1 rounded-lg border border-zinc-300 focus:border-[#1D78AB] outline-none"
            data-testid="giveaway-prize-input"
            autoFocus
          />
        </div>

        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Duration</label>
          <div className="grid grid-cols-5 gap-2 mt-1">
            {[15, 30, 60, 180, 300].map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setDuration(s)}
                className={`h-10 rounded-lg text-sm font-bold transition ${
                  duration === s
                    ? "bg-[#1D78AB] text-white"
                    : "bg-zinc-100 text-zinc-800 hover:bg-zinc-200"
                }`}
                data-testid={`giveaway-duration-${s}`}
              >
                {s < 60 ? `${s}s` : `${s / 60}m`}
              </button>
            ))}
          </div>
        </div>

        <div className="flex gap-2 pt-2">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="flex-1 h-11 rounded-lg bg-zinc-100 hover:bg-zinc-200 font-bold text-sm"
            data-testid="giveaway-cancel-btn"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            className="flex-1 h-11 rounded-lg bg-amber-400 hover:bg-amber-300 text-black font-black text-sm disabled:opacity-60"
            data-testid="giveaway-start-btn"
          >
            {busy ? "Starting…" : "Start giveaway"}
          </button>
        </div>
      </div>
    </div>
  );
}
